import React from 'react';
import dateFormat from 'dateformat';
import { get, compact, flatten } from 'lodash-es';
import { getModelState } from 'client/data/luckdragon/model';
import { AppraisalModel, buildAllStylesAppraisalFilteredPath } from 'client/data/models/appraisal';
import { buildBuyingTipsPath, BuyingTipsModel } from 'client/data/models/buying-tips';
import { ConsumerReviewsModel, ConsumerReviewsPaths } from 'client/data/models/consumer-reviews';
import { CoreLeaseDealsModel, CoreLeaseDealsPaths } from 'client/data/models/core-lease-deals';
import { DealsModel, PATHS as DEALS_PATHS } from 'client/data/models/deals';
import { EditorialModel, buildMakeArticlesPath } from 'client/data/models/editorial';
import {
  EditorialReviewModel,
  buildReviewPath,
  buildAuthorPath,
  buildPreProdContentPath,
  buildPreProdContentAuthorPath,
  buildRatedByPath,
  buildRecommendedTrimPath,
} from 'client/data/models/editorial-review';
import { EvInsightsModel, EvInsightsPaths } from 'client/data/models/ev-insights';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { getWizardIncentivesPath, IncentiveModel } from 'client/data/models/incentives';
import { InventoryModel } from 'client/data/models/inventory';
import {
  buildInventoryCountByTypePath,
  buildMMYSubmodelTypeAmountsPath,
  InventoryAggregateModel,
} from 'client/data/models/inventory-aggregate';
import {
  InventoryCoreConfigurationsModel,
  buildTrimSummariesPath,
} from 'client/data/models/inventory-core-configurations';
import { buildLeadTargetsPath, LeadTargetsModel } from 'client/data/models/lead-targets';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { MaintenanceModel, MaintenancePaths } from 'client/data/models/maintenance';
import { MediaModel, buildLatestVideosForGenerationPath, buildShortsPath } from 'client/data/models/media';
import {
  VehicleDetailsModel,
  buildComparisonsPath,
  buildQuestionsPath,
  buildWhatsNewPath,
  buildMarketAnalysisPath,
} from 'client/data/models/mmy-details';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { PageModel } from 'client/data/models/page';
import { PhotoflipperModel, buildAllPhotosPath, buildSpinPhotosPath } from 'client/data/models/photoflipper';
import { buildMMYSubmodelTrimPhotosPathWithParams, PhotosModel } from 'client/data/models/photos';
import { getRepairPalDataPath, ReliabilityModel } from 'client/data/models/reliability';
import { SafetyModel, buildSafetyRatingsPathByParams } from 'client/data/models/safety';
import { SEOModel } from 'client/data/models/seo';
import { getTreehouseEligiblePath, TreehouseModel } from 'client/data/models/treehouse';
import {
  buildBadgesPath,
  buildSegmentRatingsPath,
  TypeRankingsFeaturesModel,
} from 'client/data/models/type-rankings-features';
import { UnifiedLeadFormModel, getUnifiedLeadFormDealerCountV2Path } from 'client/data/models/unified-lead-form';
import { URL_CONTEXT_PATH, UrlModel } from 'client/data/models/url';
import {
  buildSubmodelsPath,
  buildVehiclePath as buildGraphqlVehiclePath,
  VehicleGraphQLModel,
} from 'client/data/models/vehicle-graphql';
import {
  buildMmysMsrpPricesPath,
  buildMakeModelDefaultYear,
  buildPopularStylesPath,
  VehicleModel,
  FeatureSpecsPaths,
  buildEvTestedDataPath,
  buildFuelCostsPath,
  buildMMYGenericFeaturesPath,
  buildModelFamilyIdsModelYearsPath,
  buildForSaleYearsPath,
  getUsedBaseStylePath,
  buildUsedTmvPricingPath,
} from 'client/data/models/vehicle-v2';
import { VisitorModel } from 'client/data/models/visitor';
import { LEAD_FORM_CONFIGS, STRATEGIES } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { DEALERLESS_OEM } from 'site-modules/shared/constants/oem';
import { CATEGORIES_PLAIN } from 'site-modules/shared/constants/photoflipper/photoflipper';
import { EV_TESTED_DATA_START_DATE } from 'site-modules/shared/constants/range-and-cost';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { isRoiTargetedFlavor } from 'client/utils/flavor-targeting';
import { getImageUrl, getMediaImageUrl, IMAGE_SIZE_AND_EXT_REG, SOURCES } from 'client/utils/image-helpers';
import { getQuery } from 'client/utils/location';
import { getParamsWithMakes } from 'client/utils/router-params';
import { setCriticalCss } from 'client/actions/critical-css';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getExtendedCarSchema } from 'client/seo/schemas/car';
import { getFAQSchema } from 'client/seo/schemas/faq';
import { getImageSchema } from 'client/seo/schemas/image';
import { getNewCoreTrimSchemas } from 'client/seo/schemas/new-core-trims';
import { getPersonSimpleSchema } from 'client/seo/schemas/person';
import { appendReviewSchema } from 'client/seo/schemas/review';
import { getItemListVideoSchema } from 'client/seo/schemas/video';
import {
  INCENTIVES_AD,
  BUILD_PRICE_AD,
  PRICING_AD,
  INSIGHT_AD,
  PHOTOFLIPPER_AD,
  SRP_AD,
} from 'client/site-modules/shared/components/native-ad/utils/constants';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
  unsoldSiteServedAds,
} from 'client/site-modules/shared/components/native-ad/utils/utils';
import { EdmundsNewCarsNoAdhesion } from 'client/site-modules/shared/pages/decorators/edmunds-new-cars-no-adhesion';
import {
  CORE_GALLERY_SIZE,
  SEO_IMAGE_SIZE,
  SEO_IMAGES_PAGE_SIZE,
  SEO_IMAGES_WIDTHS,
} from 'site-modules/core-page/utils/constants';
import {
  CORE_SRP_INVENTORY_COUNT,
  getInitialSearchFilter,
  getPreprodInitialSearchFilter,
} from 'site-modules/core-page/utils/core-srp';
import { getFaqEntries } from 'site-modules/core-page/utils/faq-content-generator';
import { buildCoreBreadcrumbs } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { getCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { isCoreEtrActivationEnabled } from 'site-modules/shared/utils/core-page/core-etr-activation';
import { EDMUNDS_CONTENT, getCoreMedia } from 'site-modules/shared/utils/core-page/core-media';
import { corePageDecorator } from 'site-modules/shared/utils/core-page/core-page-decorator-v2';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import {
  getPublicationStateKey,
  getBodyTypeBreadcrumbFromCategories,
} from 'site-modules/shared/utils/core-page/page-helpers';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getUsedVehiclePriceRangeFromModelState } from 'site-modules/shared/utils/core-page/pricing';
import { getBestSegmentRating } from 'site-modules/shared/utils/core-page/rating-and-ranking';
import { getCoreUrlPattern } from 'site-modules/shared/utils/core-page/url-pattern';
import { VehiclePreloader } from 'site-modules/shared/utils/core-page/vehicle-preloader-v2';
import { getOverallRating } from 'site-modules/shared/utils/editorial-helper';
import { create404Error } from 'site-modules/shared/utils/errors';
import { getEspTrustDateUnformatted } from 'site-modules/shared/utils/esp';
import { getFilterFromLocation } from 'site-modules/shared/utils/inventory/search-filter';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import {
  getUnknownLocationSearchFilter,
  MINI_INVENTORY_COUNT,
} from 'site-modules/shared/utils/mini-inventory/mini-inventory';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { isPreProd } from 'site-modules/shared/utils/publication-states';
import { SITE_URL } from 'site-modules/shared/pages/seo';
import { NewCore } from './new-core';

function getBackupDescription({ metaTagsDisplayVehicle, displayMakeModel }) {
  return `Research the ${metaTagsDisplayVehicle} with our expert reviews and ratings. Edmunds also has ${displayMakeModel} pricing, MPG, specs, pictures, safety features, consumer reviews and more. Our comprehensive coverage delivers all you need to know to make an informed car buying decision.`;
}

function getNewCoreMetaDescription({ metaTagsDisplayVehicle, priceRange, trimSummaries }) {
  const startingMsrp = get(priceRange, `[${get(priceRange, 'length', 0) - 1}]`);
  const trimCount = get(trimSummaries, 'length', 0);

  if (!startingMsrp || !trimCount) {
    return null;
  }

  const trimLabel = trimCount === 1 ? 'trim' : 'trims';

  return `${metaTagsDisplayVehicle} MSRP starts at ${getPriceStringV2(startingMsrp)}. Read expert review, compare ${trimCount} ${trimLabel}, see MPG, specs, and safety ratings. Get dealer pricing and True Market Value on Edmunds.`;
}

function preload(store, props, { isBelowFold = false, params: { isUsed } }) {
  const params = getParamsWithMakes(props);
  const query = getQuery(props.location);
  const pathname = get(props, 'location.pathname', '');
  const { mobile } = store.getState();

  const qb43Recipe = ExperimentUtil.getForcedOrAssignedRecipeName({
    state: store.getState(),
    campaignName: 'QB-43-New-Core-V3-Signup',
    defaultVal: 'ctrl',
  });
  const core7026Recipe = ExperimentUtil.getForcedOrAssignedRecipeName({
    state: store.getState(),
    campaignName: 'core-7026-found',
    defaultVal: 'ctrl',
  });
  const core7705Recipe = ExperimentUtil.getForcedOrAssignedRecipeName({
    state: store.getState(),
    campaignName: 'core-7705-fullnav',
    defaultVal: 'ctrl',
  });
  const qbNewCoreVaporwareRecipe = ExperimentUtil.getForcedOrAssignedRecipeName({
    state: store.getState(),
    campaignName: 'QB-32-New-Core-Vaporware-V2',
    defaultVal: 'ctrl',
  });
  const coreEtrActivationRecipe = ExperimentUtil.getForcedOrAssignedRecipeName({
    state: store.getState(),
    campaignName: 'core-etr-activation2',
    defaultVal: 'ctrl',
  });

  const preloader = new VehiclePreloader(store, { ...props, params }, { isSubpage: false, useGraphqlVehicle: true });

  preloader.set(URL_CONTEXT_PATH, UrlModel, {
    urlPattern: getCoreUrlPattern(params.year, params.submodel),
  });

  return preloader.load().then(modelState => {
    const makeModelSubmodelYear = modelState.get(buildGraphqlVehiclePath(params), VehicleGraphQLModel);

    const vehicleParams = {
      make: makeModelSubmodelYear.make.slug,
      model: makeModelSubmodelYear.model.slug,
      year: makeModelSubmodelYear.year,
      ...(params.submodel ? { submodel: params.submodel } : {}),
    };
    const paramsFromVehicle = getParamsFromVehicle(makeModelSubmodelYear);
    const { make, model, year, submodel } = vehicleParams;
    const pubState = getPublicationStateKey(makeModelSubmodelYear.pubStates, isUsed);
    const isPreProdPubState = pubState === 'preprod';
    const isDealerlessOem = DEALERLESS_OEM.includes(make);
    const isCore7705Enabled = !isPreProdPubState && core7705Recipe.includes('chal');
    const mmySubmodelSlug = {
      make: makeModelSubmodelYear.make.slug,
      model: makeModelSubmodelYear.model.slug,
      submodel: makeModelSubmodelYear.submodels.slug,
      year: makeModelSubmodelYear.year,
    };
    const photoParams = { ...mmySubmodelSlug, submodel: makeModelSubmodelYear.submodels.name };

    preloader.resolve('location', VisitorModel);

    // core-highlights, ev-insights, preprod-pricing, trims-comparator link
    preloader.resolve(buildPopularStylesPath(paramsFromVehicle), VehicleModel);

    // core-highlights
    preloader.resolve(buildSegmentRatingsPath({ makeSlug: make, modelSlug: model, year }), TypeRankingsFeaturesModel);

    // TODO: Review after core-7707
    preloader.resolve(buildSubmodelsPath(vehicleParams), VehicleGraphQLModel);

    // price range
    if (isUsed) {
      preloader.resolve(buildMMYSubmodelTypeAmountsPath({ ...params, isUsed }), InventoryAggregateModel);
      preloader.resolve(getUsedBaseStylePath(params), VehicleModel);
      preloader.resolve(buildAllStylesAppraisalFilteredPath(paramsFromVehicle), AppraisalModel);
    } else {
      preloader.resolve(buildMmysMsrpPricesPath(mmySubmodelSlug), VehicleModel);
    }

    preloader.resolve('seoExperiments', SEOModel);
    preloader.resolve(`content.${year}`, SEOModel);

    preloader.resolve(buildSpinPhotosPath(photoParams), PhotoflipperModel);
    preloader.resolve(
      buildInventoryCountByTypePath({
        ...vehicleParams,
        year: isPreProdPubState || isDealerlessOem ? undefined : vehicleParams.year,
      }),
      InventoryAggregateModel
    );
    preloader.resolve(
      buildBadgesPath({
        makeSlug: mmySubmodelSlug.make,
        modelSlug: mmySubmodelSlug.model,
        year: mmySubmodelSlug.year,
        submodelSlug: mmySubmodelSlug.submodel,
      }),
      TypeRankingsFeaturesModel
    );

    preloader.resolve(buildReviewPath(paramsFromVehicle), EditorialReviewModel);
    preloader.resolve(buildAuthorPath(paramsFromVehicle), EditorialReviewModel);
    preloader.resolve(buildPreProdContentPath(paramsFromVehicle), EditorialReviewModel);

    // Hero image
    const heroImageSize = mobile ? CORE_GALLERY_SIZE.MOBILE.HERO : CORE_GALLERY_SIZE.DESKTOP.HERO;
    preloader.resolve(
      buildAllPhotosPath({ ...photoParams, pageNum: 1, imageSize: heroImageSize.width }),
      PhotoflipperModel
    );

    if (['chal-1', 'chal-2', 'chal-3', 'chal-4'].includes(qb43Recipe)) {
      preloader.resolve(
        DEALS_PATHS.CSD_FRANCHISE_INVENTORY({
          make: makeModelSubmodelYear.make.slug,
          model: makeModelSubmodelYear.model.slug,
          year: makeModelSubmodelYear.year,
        }),
        DealsModel
      );
    }

    const rowImageSize = mobile ? CORE_GALLERY_SIZE.MOBILE.ROW : CORE_GALLERY_SIZE.DESKTOP.ROW;
    // Gallery row
    if (!mobile || isBelowFold) {
      preloader.resolve(
        buildAllPhotosPath({ ...photoParams, pageNum: 1, imageSize: rowImageSize.width }),
        PhotoflipperModel
      );

      preloader.resolve(
        buildAllPhotosPath({
          ...photoParams,
          pageNum: 1,
          category: CATEGORIES_PLAIN.INTERIOR,
          imageSize: rowImageSize.width,
        }),
        PhotoflipperModel
      );
    }

    // Return 404 page if user try access pre prod car with used param
    if (isPreProdPubState && pathname.indexOf('/used') > -1) {
      throw create404Error('Page not found');
    }

    const isInternational = isUnknownZip(modelState.get('location', VisitorModel));
    if (isPreProdPubState) {
      store.dispatch(legacyPageUpdate({ pageName: 'preprod_new_model_core', categoryName: 'preprod_new_model_core' }));

      preloader.resolve(
        getUnifiedLeadFormDealerCountV2Path({
          makeSlug: makeModelSubmodelYear.make.slug,
          modelSlug: makeModelSubmodelYear.model.slug,
          strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_ROI.strategy,
        }),
        UnifiedLeadFormModel
      );

      preloader.set('searchResultsFilter', InventoryModel, {
        ...getUnknownLocationSearchFilter(isInternational),
        ...getPreprodInitialSearchFilter({ makeSlug: make, modelSlug: model, year: year - 1 }),
        ...getFilterFromLocation(query),
        pageSize: MINI_INVENTORY_COUNT[!!mobile],
      });

      if (isBelowFold) {
        preloader.resolve('searchResults', InventoryModel);
      }
    }

    if (isBelowFold || isPreProdPubState) {
      preloader.resolve(buildPreProdContentAuthorPath(paramsFromVehicle), EditorialReviewModel);
    }

    if (isBelowFold) {
      preloader.resolve(buildRatedByPath(paramsFromVehicle), EditorialReviewModel);
      preloader.resolve(getTreehouseEligiblePath(), TreehouseModel);
      preloader.resolve(buildQuestionsPath(vehicleParams), VehicleDetailsModel);
      if (!isCore7705Enabled) {
        preloader.resolve(buildLatestVideosForGenerationPath(vehicleParams), MediaModel);
      }
      preloader.resolve(buildShortsPath(vehicleParams), MediaModel);
      preloader.resolve(buildForSaleYearsPath(vehicleParams), VehicleModel);
      preloader.resolve(buildMakeArticlesPath(make), EditorialModel);

      // SEO Hero
      preloader.resolve(
        buildAllPhotosPath({ ...photoParams, pageNum: 1, imageSize: SEO_IMAGE_SIZE.width }),
        PhotoflipperModel
      );
      // SEO images
      preloader.resolve(
        buildMMYSubmodelTrimPhotosPathWithParams({
          ...photoParams,
          pageSize: SEO_IMAGES_PAGE_SIZE,
          width: SEO_IMAGES_WIDTHS,
        }),
        PhotosModel
      );

      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
    }

    preloader.resolve(buildModelFamilyIdsModelYearsPath(makeModelSubmodelYear.modelFamilyIds), VehicleModel);

    if (!isPreProdPubState) {
      // core-highlights, features-highlights
      preloader.resolve(FeatureSpecsPaths.buildPartialFeaturesPath(mmySubmodelSlug), VehicleModel);

      preloader.resolve(buildRecommendedTrimPath(vehicleParams), EditorialReviewModel);

      if (isUsed) {
        preloader.set('searchResultsFilter', InventoryModel, {
          ...getInitialSearchFilter({ makeSlug: make, modelSlug: model, year }),
          ...getFilterFromLocation(query),
          pageSize: CORE_SRP_INVENTORY_COUNT[!!mobile],
          fetchSuggestedFacets: true,
        });
        preloader.resolve('searchResults', InventoryModel);
      }

      // trim-summary
      if (!isUsed) {
        preloader.resolve(buildTrimSummariesPath(mmySubmodelSlug), InventoryCoreConfigurationsModel);
      }

      preloader.set(ConsumerReviewsPaths.getVehicleFilterPath(), ConsumerReviewsModel, {
        params: {
          make,
          model,
          year,
          ...(submodel ? { submodel } : {}),
        },
      });
      preloader.set(ConsumerReviewsPaths.getConsumerReviewsFilterPath(), ConsumerReviewsModel, {
        pagenum: 1,
        pagesize: 4, // Number of consumer reviews in component
      });
      preloader.resolve(
        ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath({ make, model, year }),
        ConsumerReviewsModel
      );
      preloader.resolve(
        ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath({ make, model, year: year - 1 }),
        ConsumerReviewsModel
      );
      preloader.resolve(ConsumerReviewsPaths.getConsumerReviewsPath(), ConsumerReviewsModel);

      preloader.resolve(
        CoreLeaseDealsPaths.getFirstBestPaymentDealMMYPath({
          make: makeModelSubmodelYear.make.name,
          model: makeModelSubmodelYear.model.name,
          year,
        }),
        CoreLeaseDealsModel
      );

      preloader.resolve(buildFuelCostsPath(makeModelSubmodelYear.defaultStyleId), VehicleModel);

      const styleAttributes = makeModelSubmodelYear.styleAttributes;
      const adsSet = new Set(
        compact([
          INCENTIVES_AD.AD_NAME,
          BUILD_PRICE_AD.AD_NAME,
          !isUsed && PHOTOFLIPPER_AD.AD_NAME,
          !isUsed && PRICING_AD.AD_NAME,
          !isPreProdPubState && (styleAttributes.isElectric || styleAttributes.isPluginHybrid) && INSIGHT_AD.AD_NAME,
          isUsed && SRP_AD.AD_NAME,
        ])
      );
      const vehiclePageContext = modelState.get('vehicle', PageModel);
      setAdsListToPreload(preloader, store.getState(), adsSet, vehiclePageContext);

      if (isCore7705Enabled) {
        preloader.resolve(buildLatestVideosForGenerationPath(vehicleParams), MediaModel);
        preloader.resolve(buildWhatsNewPath(vehicleParams), VehicleDetailsModel);
        preloader.resolve(buildMarketAnalysisPath(vehicleParams), VehicleDetailsModel);
        preloader.resolve(buildBuyingTipsPath(vehicleParams), BuyingTipsModel);
      }

      if (isBelowFold) {
        // reliability
        preloader.resolve(getRepairPalDataPath({ makeSlug: make, modelSlug: model, year }), ReliabilityModel);
        preloader.resolve(
          MaintenancePaths.buildMaintenanceRecallsPath(makeModelSubmodelYear.modelYearId),
          MaintenanceModel
        );
        preloader.resolve(ConsumerReviewsPaths.buildReviewsSummaryPath(vehicleParams), ConsumerReviewsModel);

        if (!isCore7705Enabled) {
          preloader.resolve(buildWhatsNewPath(vehicleParams), VehicleDetailsModel);

          // buying tips
          preloader.resolve(buildMarketAnalysisPath(vehicleParams), VehicleDetailsModel);
          preloader.resolve(buildBuyingTipsPath(vehicleParams), BuyingTipsModel);
        }
        // range and cost
        preloader.resolve(buildPopularStylesPath(vehicleParams), VehicleModel);
        // safety
        preloader.resolve(buildSafetyRatingsPathByParams({ params: vehicleParams }), SafetyModel);
        // vehicle-comparisons
        preloader.resolve(buildComparisonsPath(vehicleParams), VehicleDetailsModel);
        // scorecard section
        preloader.resolve(
          buildMMYSubmodelTrimPhotosPathWithParams({
            ...photoParams,
            shottype: 'default',
            width: '500',
            category: CATEGORIES_PLAIN.INTERIOR,
            pageSize: 5,
          }),
          PhotosModel
        );

        preloader.resolve(buildMMYGenericFeaturesPath(vehicleParams), VehicleModel);

        // !reviewSummary
        preloader.resolve(
          ConsumerReviewsPaths.buildConsumerReviewsAspectsPath(makeModelSubmodelYear),
          ConsumerReviewsModel
        );

        if (makeModelSubmodelYear.styleAttributes.isElectric || makeModelSubmodelYear.styleAttributes.isPluginHybrid) {
          preloader.resolve(
            EvInsightsPaths.getEvInsightsDataPath(makeModelSubmodelYear.defaultStyleId),
            EvInsightsModel
          );
          preloader.resolve(getWizardIncentivesPath(makeModelSubmodelYear.defaultStyleId), IncentiveModel);

          if (year >= EV_TESTED_DATA_START_DATE && !makeModelSubmodelYear.styleAttributes.isPluginHybrid) {
            preloader.resolve(buildEvTestedDataPath([makeModelSubmodelYear.defaultStyleId]), VehicleModel);
          }
        }

        if (
          Object.keys(get(makeModelSubmodelYear, 'edmundsTypeCategories', {})).includes('electric') &&
          year >= EV_TESTED_DATA_START_DATE
        ) {
          preloader.resolve(buildEvTestedDataPath(makeModelSubmodelYear.styleIds), VehicleModel);
        }
      }
    }

    return preloader.load().then(async modelStateInstance => {
      const trimSummaries = modelStateInstance.get(
        buildTrimSummariesPath(mmySubmodelSlug),
        InventoryCoreConfigurationsModel
      );
      const isCore7026Enabled = !isPreProdPubState && core7026Recipe === 'chal';

      if (!isPreProdPubState) {
        if (trimSummaries?.[0]?.hasLeadFormDealers && !isInternational && isCore7026Enabled) {
          const strategy = isRoiTargetedFlavor() ? STRATEGIES.TRIM_DLINV_DST_TARGET_ROI : STRATEGIES.TRIM_DLINV_DST;

          preloader.resolve(
            buildLeadTargetsPath({
              make: makeModelSubmodelYear.make.name,
              model: makeModelSubmodelYear.model.name,
              year,
              styleId: trimSummaries[0].styleId,
              strategy,
              radius: 100,
              apiSubModel: makeModelSubmodelYear.submodels.slug,
              encodedTrim: trimSummaries[0].trimName,
            }),
            LeadTargetsModel
          );
        }

        // price range
        if (isUsed) {
          const submodelAmounts = modelStateInstance.get(
            buildMMYSubmodelTypeAmountsPath({ ...params, isUsed }),
            InventoryAggregateModel
          );
          const hasPriceRange = get(submodelAmounts, `priceRanges["${makeModelSubmodelYear.submodels.slug}"]`);

          if (isInternational || !hasPriceRange) {
            const usedBaseStyleId = modelStateInstance.get(
              `${getUsedBaseStylePath(params)}.usedDefaultStyle.id`,
              VehicleModel
            );

            preloader.resolve(buildUsedTmvPricingPath(usedBaseStyleId), VehicleModel);
          }
        }
      }

      const vehiclePageContext = modelStateInstance.get('vehicle', PageModel);
      // Hero photo preload: start
      const firstContent = modelStateInstance.get(buildPreProdContentPath(paramsFromVehicle), EditorialReviewModel);
      const editorialReview = modelStateInstance.get(buildReviewPath(paramsFromVehicle), EditorialReviewModel);
      const editorialReviewHero = get(editorialReview, 'heroImage');

      const photos = modelStateInstance.get(
        buildAllPhotosPath({
          ...photoParams,
          pageNum: 1,
          imageSize: heroImageSize.width,
        }),
        PhotoflipperModel
      );

      const { heroImage } = getCoreMedia({
        params,
        vehicle: makeModelSubmodelYear,
        isPreprod: isPreProdPubState,
        firstContent,
        editorialReviewHero,
        photos,
        defaultSize: heroImageSize,
      });

      const assetsToPreload = [{ href: getImageUrl(heroImage.src, undefined, SOURCES.MEDIA), as: 'image' }];

      preloader.set('server.preloadedAssets', PageModel, assetsToPreload);
      // Hero photo preload: end

      const hasEditorialRating = !!getOverallRating(editorialReview);
      const hasPreprodRating =
        isPreProdPubState && !hasEditorialRating && !!editorialReview?.firstContent?.firstImpressionScore;
      const hasTypeRating = !!get(
        getBestSegmentRating(
          modelStateInstance.get(
            buildSegmentRatingsPath({ makeSlug: make, modelSlug: model, year }),
            TypeRankingsFeaturesModel
          ),
          { submodelSlug: mmySubmodelSlug.submodel }
        ),
        'rank'
      );
      const hasConsumerRating = !!get(
        modelStateInstance.get(
          ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath({ make, model, year }),
          ConsumerReviewsModel
        ),
        'ratingAggregation.averageStars'
      );

      const siteServedAds = modelStateInstance.get(
        getCreativeConfigAdPathByVehicle(mobile, vehiclePageContext),
        NativeAdsCreativeConfigModel
      );
      preloader.set(
        'isUnsoldBuildAndPriceAd',
        NativeAdsCreativeConfigModel,
        unsoldSiteServedAds(siteServedAds, [BUILD_PRICE_AD.AD_NAME])
      );

      const coreEtrActivation =
        !isPreProdPubState &&
        isCoreEtrActivationEnabled(
          mmySubmodelSlug.make,
          mmySubmodelSlug.model,
          mmySubmodelSlug.year,
          mmySubmodelSlug.submodel
        )
          ? coreEtrActivationRecipe
          : 'ctrl';

      store.dispatch(
        setCriticalCss({
          pubState,
          ...siteServedAdsCriticalCss(siteServedAds, [INCENTIVES_AD.AD_NAME, BUILD_PRICE_AD.AD_NAME]),
          hasRatings: `${hasEditorialRating},${hasTypeRating},${hasConsumerRating},${hasPreprodRating}`,
          qbNewCoreVaporwareRecipe,
          ...(isPreProdPubState ? {} : { core7705Recipe, isCore7026Enabled, coreEtrActivation }),
        })
      );

      return preloader.load();
    });
  });
}

export const getPageOptions = params => ({
  ...(params.isUsed
    ? { name: 'used_model_core_review', category: 'used_model_core' }
    : { name: 'new_model_core', category: 'new_model_core' }),
  chunkName: 'new_model_core',
  preloadAboveFold: (store, props) => preload(store, props, { params }),
  preload: (store, props) => preload(store, props, { params, isBelowFold: true }),
  seo: state => {
    /**
     * If year is not included in params, this page is being loaded from a /make/model route.
     * The preloaded vehicle should be the latest non-pre-prod vehicle in that case, and
     * all SEO should use the params from that vehicle instead (clonedParams) unless otherwise
     * indicated (breadcrumbs).
     */
    const modelState = getModelState(state);
    const makeModelSubmodelYear = modelState.get(buildGraphqlVehiclePath(params), VehicleGraphQLModel);
    const seoContentAPIResponse = makeModelSubmodelYear?.year
      ? modelState.get(`content.${makeModelSubmodelYear.year}`, SEOModel)
      : modelState.get(`content`, SEOModel);

    const isUsed = params.isUsed;
    const clonedParams = {
      ...params,
      year: makeModelSubmodelYear && makeModelSubmodelYear.year,
    };

    const editorialReview = modelState.get(buildReviewPath(clonedParams), EditorialReviewModel);
    const consumerReviews = modelState.get(ConsumerReviewsPaths.getConsumerReviewsPath(), ConsumerReviewsModel);
    const preProdContent = modelState.get(buildPreProdContentPath(clonedParams), EditorialReviewModel);

    const pageUrl = `${SITE_URL}${getCoreUrl(
      { makeSlug: params.make, modelSlug: params.model, year: params.year },
      isUsed
    )}`;

    const seoObject = { jsonld: [] };

    if (makeModelSubmodelYear) {
      const mmySubmodelSlug = {
        make: makeModelSubmodelYear.make.slug,
        model: makeModelSubmodelYear.model.slug,
        submodel: makeModelSubmodelYear.submodels.slug,
        year: makeModelSubmodelYear.year,
      };
      const photoParams = {
        ...mmySubmodelSlug,
        submodel: makeModelSubmodelYear.submodels.name,
      };
      const pubState = getPublicationStateKey(makeModelSubmodelYear.pubStates, isUsed);
      const isPreProdPubState = pubState === 'preprod';
      const displayVehicle = buildDisplayVehicle({
        makeModelSubmodelYear,
        params: clonedParams,
        disableTrademarkCharacter: true,
        usedPrefix: '',
      });
      const displayMakeModel = buildDisplayVehicle({
        makeModelSubmodelYear,
        params: clonedParams,
        showYear: false,
        disableTrademarkCharacter: true,
        updateChevroletMakeName: true,
        usedPrefix: '',
      });
      const featuresAndSpecs = modelState.get(
        FeatureSpecsPaths.buildPartialFeaturesPath(mmySubmodelSlug),
        VehicleModel
      );
      const trimSummaries = modelState.get(buildTrimSummariesPath(mmySubmodelSlug), InventoryCoreConfigurationsModel);
      const submodels = modelState.get(buildSubmodelsPath(mmySubmodelSlug), VehicleGraphQLModel) || [];
      const seoExperiments = modelState.get('seoExperiments', SEOModel) || [];
      const isSeot5678Enabled =
        modelState.get('enable-seot-5678-trim-saliency', FeatureFlagModel) &&
        seoExperiments.includes('SEOT-5678-Trim-Saliency');
      const styleAttributes = featuresAndSpecs?.[0]?.styleAttributes;

      const priceRange = isUsed
        ? getUsedVehiclePriceRangeFromModelState({
            modelState,
            params,
            submodelSlug: makeModelSubmodelYear.submodels.slug,
          })
        : modelState.get(buildMmysMsrpPricesPath(mmySubmodelSlug), VehicleModel);

      const metaTagsDisplayVehicle = buildDisplayVehicle({
        makeModelSubmodelYear,
        params: clonedParams,
        disableTrademarkCharacter: true,
        updateChevroletMakeName: true,
        usedPrefix: '',
      });

      seoObject.description = getBackupDescription({
        metaTagsDisplayVehicle,
        displayMakeModel,
      });
      seoObject.title = `${metaTagsDisplayVehicle} Prices, Reviews, and Pictures | Edmunds`;

      if (isPreProd(makeModelSubmodelYear.pubStates)) {
        const metaDescription = get(preProdContent, 'metaDescription', '');

        seoObject.description = metaDescription
          ? metaDescription.concat(' Read more at Edmunds.com.')
          : `Edmunds has the latest news on the ${metaTagsDisplayVehicle}, including pricing information, release date, review, pictures, specs and first impressions from our automotive experts.`;

        seoObject.type = 'article';
        seoObject.publishedTime = get(preProdContent, 'publishDate');
        seoObject.modifiedTime = get(preProdContent, 'dateModified');
        seoObject.articleAuthor = get(preProdContent, 'author.contentMetadata.authorName');
      } else {
        const newCoreMetaDescription =
          !isUsed &&
          getNewCoreMetaDescription({
            metaTagsDisplayVehicle,
            priceRange,
            trimSummaries,
          });

        if (newCoreMetaDescription) {
          seoObject.description = newCoreMetaDescription;
        } else if (params.submodel) {
          seoObject.description = seoObject.description.concat(' Read more at Edmunds.com.');
        }
      }

      const buyingTips = modelState.get(buildBuyingTipsPath(mmySubmodelSlug), BuyingTipsModel);
      const whatsNewContent = modelState.get(buildWhatsNewPath(clonedParams), VehicleDetailsModel);
      const editorialReviewAuthor = modelState.get(buildAuthorPath(mmySubmodelSlug), EditorialReviewModel);
      const defaultYear = modelState.get(buildMakeModelDefaultYear(mmySubmodelSlug), VehicleModel);
      seoObject.jsonld.push(
        getBreadcrumbSchema(
          buildCoreBreadcrumbs({
            makeModelSubmodelYear,
            params,
            defaultYear,
            includeLastLink: true,
            isMainCorePage: true,
            typeBreadcrumb: getBodyTypeBreadcrumbFromCategories(get(makeModelSubmodelYear, 'edmundsTypeCategories')),
          })
        )
      );

      const seoImages = modelState.get(
        buildMMYSubmodelTrimPhotosPathWithParams({
          ...photoParams,
          pageSize: SEO_IMAGES_PAGE_SIZE,
          width: SEO_IMAGES_WIDTHS,
        }),
        PhotosModel
      );
      const images = seoImages?.map(({ href }) => ({
        image: getMediaImageUrl(href),
        thumb: getMediaImageUrl(href.replace(IMAGE_SIZE_AND_EXT_REG, '_175.jpg')),
      }));

      const espDateModified = isPreProdPubState ? undefined : dateFormat(getEspTrustDateUnformatted(), 'yyyy-mm-dd');
      const customDate = buyingTips?.created || espDateModified;
      const firstContent = modelState.get(
        buildPreProdContentPath(getParamsFromVehicle(makeModelSubmodelYear)),
        EditorialReviewModel
      );
      const editorialReviewHero = get(editorialReview, 'heroImage');
      const heroPhotos = modelState.get(
        buildAllPhotosPath({
          ...photoParams,
          pageNum: 1,
          imageSize: SEO_IMAGE_SIZE.width,
        }),
        PhotoflipperModel
      );
      const { heroImage } = getCoreMedia({
        params,
        vehicle: makeModelSubmodelYear,
        isPreprod: isPreProdPubState,
        firstContent,
        editorialReviewHero,
        photos: heroPhotos,
        defaultSize: SEO_IMAGE_SIZE,
      });
      const seoHeroUrl = getImageUrl(heroImage.src, undefined, SOURCES.MEDIA);
      const isEdmundsPhoto = heroImage?.provider === EDMUNDS_CONTENT;

      const carSchema = appendReviewSchema(
        getExtendedCarSchema({
          makeModelSubmodelYear,
          editorialReview,
          consumerReviews,
          seoObject,
          priceRange,
          whatsNewContent,
          style: get(featuresAndSpecs, '[0]'),
          url: pageUrl,
          images,
          heroImage: seoHeroUrl,
          isCorePage: true,
        }),
        {
          params: {
            ...clonedParams,
            isElectric: !!styleAttributes?.electric,
            isPluginHybrid: !!styleAttributes?.pluginElectric,
            isTruck: !!styleAttributes?.truck,
          },
          makeModelSubmodelYear,
          editorialReview,
          author: editorialReviewAuthor,
          consumerReviews,
          priceRange,
          hasItemReviewed: false,
          thumbUrl: images?.[0]?.thumb,
          customDateModified: customDate ? dateFormat(customDate, 'yyyy-mm-dd') : null,
          isCorePage: true,
        }
      );

      const trimSchemas =
        !isUsed && !isPreProdPubState && isSeot5678Enabled
          ? getNewCoreTrimSchemas({
              trimSummaries,
              submodels,
              makeModelSubmodelYear,
              pageUrl,
            })
          : null;
      seoObject.jsonld.push(
        trimSchemas?.offers.length ? { ...carSchema, offers: trimSchemas.offers } : carSchema,
        trimSchemas?.itemList
      );

      seoObject.jsonld.push(
        getImageSchema({
          uri: seoHeroUrl,
          title: displayVehicle,
          isEdmundsPhoto,
          author: getPersonSimpleSchema(heroImage?.author),
        })
      );
      seoObject.image = {
        url: seoHeroUrl,
        alt: displayVehicle,
        ...SEO_IMAGE_SIZE,
      };

      const videos = modelState.get(buildLatestVideosForGenerationPath(mmySubmodelSlug), MediaModel);

      if (videos && videos.length) {
        seoObject.jsonld.push(getItemListVideoSchema(videos, { noTranscript: isPreProdPubState }));
      }

      if (!isPreProd(makeModelSubmodelYear.pubStates)) {
        seoObject.jsonld.push(
          getFAQSchema({
            entries: getFaqEntries({
              vehicle: makeModelSubmodelYear,
              editorialReview,
              consumerReviews,
              whatsNewContent,
              stylesList: featuresAndSpecs,
            }),
            generateLearnMoreLinks: true,
            pathname: getCoreUrl({
              makeSlug: params.make,
              modelSlug: params.model,
              year: get(makeModelSubmodelYear, 'year'),
            }),
          })
        );
      }
    }

    seoObject.jsonld = compact(flatten(seoObject.jsonld));
    const apiDescription = get(seoContentAPIResponse, 'metaDescription');
    if (apiDescription) {
      seoObject.description = apiDescription;
    }

    return seoObject;
  },
  component: props => <NewCore {...props} params={params} />,
  decorator: EdmundsNewCarsNoAdhesion,
  options: {
    disableScrollToTop: true,
    hasVehicleContext: true,
    useGraphqlVehicle: true,
  },
});

export default params => corePageDecorator(getPageOptions(params), params);
